<!--
  Custom icon: Shield with key — for permissions / security menus.
  Demonstrates how to make a MULTI-COLOR icon that adapts to dark / light theme.

  Two techniques shown here; pick the one you prefer:

  ① Reactive computed color (uses themeStore directly) — most flexible.
     Different SVG paths can have completely different dark vs light colors.

  ② CSS variable fallback (works even without JS theme store, pure CSS).
     Requires defining `--icon-accent` and `--icon-base` in your global CSS.

  Usage:
    <!-- single-color mode (overrides accent with parent color) -->
    <el-icon :size="24"><IconShieldKey /></el-icon>

    <!-- pass isDark explicitly when you don't want to import the store -->
    <IconShieldKey :is-dark="true" :size="20" />
-->
<template>
  <!-- NOTE: do NOT set width/height here — let el-icon control sizing -->
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <!-- Shield outline — uses currentColor so el-icon color prop works -->
    <path
      fill="currentColor"
      opacity="0.85"
      d="M12 2L3 6v6c0 5.25 3.75 10.15 9 11.35C17.25 22.15 21 17.25 21 12V6L12 2z"
    />
    <!-- Inner highlight — uses a theme-reactive accent color -->
    <path
      :fill="accentColor"
      d="M12 4.35L5 7.8V12c0 4.05 2.9 7.85 7 8.9 4.1-1.05 7-4.85 7-8.9V7.8L12 4.35z"
      opacity="0.6"
    />
    <!-- Key symbol on top -->
    <circle cx="12" cy="10.5" r="2.2" :stroke="keyColor" stroke-width="1.5" fill="none" />
    <line x1="14.2" y1="10.5" x2="17" y2="10.5" :stroke="keyColor" stroke-width="1.5" stroke-linecap="round" />
    <line x1="16" y1="10.5" x2="16" y2="12" :stroke="keyColor" stroke-width="1.5" stroke-linecap="round" />
    <line x1="17" y1="10.5" x2="17" y2="12" :stroke="keyColor" stroke-width="1.5" stroke-linecap="round" />
  </svg>
</template>

<script setup>
import { computed } from 'vue'
import { useThemeStore } from '@/store/theme'

const props = defineProps({
  /** Override theme detection — useful when embedding outside the portal shell */
  isDark: {
    type: Boolean,
    default: null,
  },
})

const themeStore = useThemeStore()

// Resolve whether we are in dark mode:
// explicit prop → store → default dark
const dark = computed(() =>
  props.isDark !== null ? props.isDark : themeStore.isDark
)

// Technique ①: fully reactive computed colors per theme
const accentColor = computed(() =>
  dark.value ? '#a78bfa' : '#6366f1'   // violet-400 vs indigo-500
)

const keyColor = computed(() =>
  dark.value ? '#f9fafb' : '#ffffff'   // near-white, slightly warmer in dark
)
</script>
